'use client';

import { datadogRum } from '@datadog/browser-rum';
import useScrollToView from '@ria-client/hooks/dom/useScrollToView';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from '@ria-client/tw-shadcn/components/table';
import { startTransition, useEffect, useRef, useState } from 'react';
import type { SummaryTableRowWithDrivers } from '../types';
import { Row } from './row';
import { SkeletonRow } from './skeleton-row';

export interface MobileFirstSummaryTableProps {
  rows: SummaryTableRowWithDrivers[];
  maxRows?: number;
  numTruncatedRows?: number;
  allowTruncatingRows?: boolean;
  showSkeleton?: boolean;
}

export function MobileFirstSummaryTable({
  rows,
  maxRows,
  numTruncatedRows,
  allowTruncatingRows,
  showSkeleton = false,
}: MobileFirstSummaryTableProps) {
  const [rowsAreTruncated, setRowsAreTruncated] = useState(true);
  const truncationEnabled = Boolean(allowTruncatingRows && maxRows && rows.length > maxRows);
  const visibleRows =
    truncationEnabled && rowsAreTruncated ? rows.slice(0, numTruncatedRows) : rows;
  const firedSummaryTableShown = useRef(false);

  useEffect(() => {
    if (!showSkeleton && !firedSummaryTableShown.current) {
      datadogRum.addTiming('summary_table_shown');
      firedSummaryTableShown.current = true;
    }
  }, [showSkeleton]);

  const toggleRows = () => {
    if (!rowsAreTruncated) {
      useScrollToView('mobile-first-summary-table-headers', 'smooth', 0);
    }
    startTransition(() => setRowsAreTruncated(value => !value));
  };

  return (
    <>
      <TableContainer className="overflow-hidden rounded-t-[8px] border border-[var(--content-neutral-lighter-2)]">
        <Table>
          <TableHeader id="mobile-first-summary-table-headers">
            <TableRow>
              <TableHead
                className="w-1/2 border-r border-b border-[var(--content-neutral-lighter-2)] bg-muted py-2 pr-4 pl-3 text-center first:pl-3"
                scope="col"
              >
                <span className="typography-text-small font-medium uppercase">Bank</span>
              </TableHead>
              <TableHead
                className="w-1/2 border-b border-l-0 border-[var(--content-neutral-lighter-2)] bg-white py-2 text-center"
                scope="col"
              >
                <span className="typography-text-small font-medium uppercase">Details</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.map((row, index) =>
              showSkeleton ? (
                <SkeletonRow
                  driversLength={row.marketplaceEntity.drivers.length}
                  key={`skeleton-${index}`}
                />
              ) : (
                <Row index={index} key={row.marketplaceEntity.id} row={row} />
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {truncationEnabled ? (
        <button
          className="typography-text-small mt-1 font-medium text-primary"
          onClick={toggleRows}
          type="button"
        >
          {rowsAreTruncated ? 'View more' : 'View fewer'}
        </button>
      ) : null}
    </>
  );
}
